import { ArrowLeftRight, Minus, Plus, RotateCcw } from 'lucide-react';
import * as React from 'react';
import styled from 'styled-components';
import { COLOR_SCALE_PRESETS, ColorScale, ColorScaleKind, ColorStop, DEFAULT_DATE_SPAN, ScaleRange, clamp01, cssGradient, defaultColorScale, presetOf, relativeScale, scaleRange } from '../../containers_v2/map/colorScale';
import { FlexDiv } from '../../containers_v2/products/style';
import { BorderColor, DarkGreySidely2, PrimaryColor, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import Dropdown from '../dropdown/Dropdown';
import { HexField, IconButton, Label, SmallInput, Swatch } from './style';

// The editor of a column's map colour scale: a bar showing the scale with
// one handle per stop (dragged along the bar or set in the list under it
// as a position and a colour), a stop added or removed, the colours
// reversed, presets to start from, and how the stops are placed: as a
// share of the values on the map or of fixed bounds, or directly at values
// of the column (the first and last stops then bound the scale, so they are
// typed rather than dragged). Nothing set means the map's historical white
// to green, and the reset goes back to it.
//
// The stops keep the order they were created in: a row keeps its input (and
// the focus in it) when its stop moves past another, and the list shows them
// along the bar through the rows' CSS order.
//
// A date's stops are days from today, negative when gone by; their rows
// say so in words, a side of today picked in a list and the days typed
// without a sign: "il y a 30 jours", "aujourd'hui", "dans 30 jours".

type Placement = 'auto' | 'fixed' | 'values';

// where a date's stop stands from today
type DateSide = 'ago' | 'today' | 'ahead';

const DEFAULT_SPAN: ScaleRange = { min: 0, max: 100 };

// the cells of a date's stop, in the order the language puts the number,
// the days and the side of today: "{{SIDE}} {{N}} jours", "{{N}} days {{SIDE}}"
const DATE_STOP_PARTS = /(\{\{SIDE\}\}|\{\{N\}\})/;

const BarWrapper = styled.div`
	position: relative;
	padding-top: 12px;
	padding-bottom: 2px;
	// room for the handles overflowing the bar's ends
	margin: 0 7px;
`;

const Bar = styled.div`
	height: 26px;
	border-radius: 6px;
	border: 1px solid ${BorderColor};
	cursor: copy;
`;

const Handle = styled.div<{ color: string, selected: boolean, fixed: boolean }>`
	position: absolute;
	top: 2px;
	width: 16px;
	height: 16px;
	transform: translateX(-50%);
	border-radius: ${p => p.fixed ? '50%' : '4px'};
	background-color: ${p => p.color};
	border: 2px solid ${SurfaceColor};
	box-shadow: 0 0 0 1px ${p => p.selected ? PrimaryColor : BorderColor}, 0 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
	cursor: ${p => p.fixed ? 'pointer' : 'grab'};
	touch-action: none;
	z-index: ${p => p.selected ? 2 : 1};
	&:active {
		cursor: ${p => p.fixed ? 'pointer' : 'grabbing'};
	}
`;

const StopRow = styled.div<{ selected: boolean }>`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 6px;
	border-radius: 6px;
	background-color: ${p => p.selected ? `color-mix(in oklab, ${PrimaryColor} 8%, transparent)` : 'transparent'};
`;

// the first column of the list: a position or a value, its unit after it,
// wide enough for a large number and the same in both cases so the
// swatches line up; wider for a date's words
const StopValueCell = styled.div<{ wide: boolean }>`
	display: flex;
	align-items: center;
	gap: 6px;
	width: ${p => p.wide ? '244px' : '180px'};
	flex-shrink: 0;
`;

const StopValueInput = styled(SmallInput)`
	width: 120px;
	flex-shrink: 0;
	text-align: right;
`;

const DaysInput = styled(SmallInput)`
	width: 60px;
	flex-shrink: 0;
	text-align: right;
`;

const BoundInput = styled(SmallInput)`
	width: 110px;
`;

const PresetChip = styled.div<{ active: boolean }>`
	width: 64px;
	height: 18px;
	border-radius: 5px;
	border: 1px solid ${p => p.active ? PrimaryColor : BorderColor};
	box-shadow: ${p => p.active ? `0 0 0 1px ${PrimaryColor}` : 'none'};
	cursor: pointer;
`;

const Unit = styled.span`
	font-size: 12px;
	color: ${DarkGreySidely2};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

function placementOf(scale: ColorScale): Placement {
	if (scale.unit === 'value') return 'values';
	return scale.range ? 'fixed' : 'auto';
}

/** A stop's share of the range mapped onto it, as the stop's `at` in the scale's unit. */
function placed(share: number, scale: ColorScale, span: ScaleRange): number {
	return scale.unit === 'value' ? span.min + share * (span.max - span.min) : share;
}

export default function ColorScaleEditor(props: {
	scale: ColorScale | undefined,
	onChange: (scale: ColorScale | undefined) => void,
	// the column is a date: a stop at a value is a number of days from today
	date?: boolean,
}) {
	const date = props.date ?? false;
	const scale = props.scale ?? defaultColorScale(date);
	const stops = scale.stops;
	const atValues = scale.unit === 'value';
	const dateRows = date && atValues;
	// the range the bar spans, in the column's unit when the stops are at values
	const span = scaleRange(scale, DEFAULT_SPAN);
	// each stop's share of the bar, by stop index
	const shares = React.useMemo(() => {
		const sorted = relativeScale(scale, DEFAULT_SPAN).stops;
		const order = stops.map((_, i) => i).sort((a, b) => stops[a].at - stops[b].at);
		const result: { share: number, rank: number }[] = [];
		order.forEach((index, rank) => { result[index] = { share: sorted[rank].at, rank }; });
		return result;
	}, [stops, scale.unit, scale.range]);
	const [selected, setSelected] = React.useState<number>(0);
	// the side of today a date's stop was put on while it stands at 0, by
	// stop index: any other value says it by its sign
	const [sides, setSides] = React.useState<(DateSide | undefined)[]>([]);
	const barRef = React.useRef<HTMLDivElement>(null);

	const kindOptions = React.useMemo(() => (['gradient', 'steps'] as ColorScaleKind[]).map(kind => ({ label: translateToString(`color_scale.kind_${kind}`), value: kind })), []);
	const placementOptions = React.useMemo<{ label: string, value: Placement }[]>(() => [
		{ label: translateToString('color_scale.range_auto'), value: 'auto' },
		{ label: translateToString('color_scale.range_fixed'), value: 'fixed' },
		{ label: translateToString('color_scale.range_values'), value: 'values' },
	], []);
	const sideOptions = React.useMemo<{ label: string, value: DateSide }[]>(() => (['ago', 'today', 'ahead'] as DateSide[]).map(side => ({ label: translateToString(`color_scale.side_${side}`), value: side })), []);

	const set = (patch: Partial<ColorScale>) => props.onChange({ ...scale, ...patch });
	const updateStop = (index: number, patch: Partial<ColorStop>) => set({ stops: stops.map((stop, i) => i === index ? { ...stop, ...patch } : stop) });
	const shareFromPointer = (clientX: number) => {
		const rect = barRef.current?.getBoundingClientRect();
		if (!rect || rect.width === 0) return 0;
		return clamp01((clientX - rect.left) / rect.width);
	};
	// a dragged stop lands on a round number: a hundredth of the bar, or a
	// unit (a hundredth under a hundred units, always a day) when the stops
	// are at values
	const snapped = (share: number) => {
		if (!atValues) return Math.round(share * 100) / 100;
		const precision = date || span.max - span.min >= 100 ? 1 : 100;
		return Math.round(placed(share, scale, span) * precision) / precision;
	};
	const sideOf = (index: number): DateSide => stops[index].at < 0 ? 'ago' : stops[index].at > 0 ? 'ahead' : sides[index] ?? 'today';
	const withSide = (index: number, side: DateSide) => { const next = [...sides]; next[index] = side; setSides(next); };
	// the stop crosses to the other side of today, or lands on it
	const setSide = (index: number, side: DateSide) => {
		withSide(index, side);
		const days = Math.abs(stops[index].at);
		updateStop(index, { at: side === 'ago' ? -days : side === 'today' ? 0 : days });
	};
	// the days typed, on the side the stop is on; 0 keeps the side
	const setDays = (index: number, days: number) => {
		const side = sideOf(index);
		withSide(index, side);
		updateStop(index, { at: side === 'ago' ? -Math.abs(days) : Math.abs(days) });
	};
	// the first and last stops of a scale at values bound it: dragging them would move the bar under them
	const fixedOnBar = (index: number) => atValues && (shares[index].rank === 0 || shares[index].rank === stops.length - 1);
	const addStopAt = (share: number) => {
		const stop: ColorStop = { at: placed(share, scale, span), color: stops[selected]?.color ?? stops[stops.length - 1].color };
		setSelected(stops.length);
		set({ stops: [...stops, stop] });
	};
	const addStop = () => {
		// in the widest gap, so the new handle never lands on another
		const sorted = [...shares].sort((a, b) => a.share - b.share);
		let widest = { share: 0.5, width: -1 };
		for (let i = 1; i < sorted.length; i++) {
			const width = sorted[i].share - sorted[i - 1].share;
			if (width > widest.width) widest = { share: (sorted[i].share + sorted[i - 1].share) / 2, width };
		}
		addStopAt(widest.share);
	};
	const removeStop = (index: number) => {
		if (stops.length <= 2) return;
		setSelected(Math.min(index, stops.length - 2));
		setSides(sides.filter((_, i) => i !== index));
		set({ stops: stops.filter((_, i) => i !== index) });
	};
	// mirrored along the bar: the bounds stay, the colours swap ends
	const flip = () => set({ stops: stops.map(stop => ({ ...stop, at: atValues ? span.min + span.max - stop.at : 1 - stop.at })) });
	const applyPreset = (preset: ColorStop[]) => {
		setSelected(0);
		set({ stops: preset.map(stop => ({ ...stop, at: placed(stop.at, scale, span) })) });
	};
	const place = (placement: Placement) => {
		if (placement === placementOf(scale)) return;
		const relative = relativeScale(scale, DEFAULT_SPAN);
		if (placement === 'values') {
			// each stop at the value it had: of the bounds, else of a span to edit
			const from = scale.range ?? (date ? DEFAULT_DATE_SPAN : DEFAULT_SPAN);
			props.onChange({ ...scale, unit: 'value', range: null, stops: stops.map(stop => ({ ...stop, at: from.min + stop.at * (from.max - from.min) })) });
			return;
		}
		// back to shares: of the bounds the values gave, when they are kept
		const range = placement === 'fixed' ? (atValues ? relative.range : scale.range ?? DEFAULT_SPAN) : null;
		const order = stops.map((_, i) => i).sort((a, b) => stops[a].at - stops[b].at);
		const relativeStops: ColorStop[] = [];
		order.forEach((index, rank) => { relativeStops[index] = relative.stops[rank]; });
		props.onChange({ ...scale, unit: undefined, range, stops: atValues ? relativeStops : stops });
	};
	// a date's stop in words: the side of today, and the days unless it is today
	const dateStopCells = (index: number, stop: ColorStop) => {
		const side = sideOf(index);
		const sideList = <Dropdown<DateSide>
			key='side'
			name={`DD-color-scale-side-${index}`}
			datalist={sideOptions}
			selectedValue={sideOptions.find(option => option.value === side)}
			onChange={value => setSide(index, value.value)}
			readOnly
			dropdownStyle={{ width: '130px', optionWidth: '130px', containerHeight: '28px' }}
		/>;
		if (side === 'today') return <>{sideList}<Unit>{translateToString('color_scale.today_date')}</Unit></>;
		return translateToString('color_scale.date_stop').split(DATE_STOP_PARTS).map((part, p) => {
			if (part === '{{SIDE}}') return sideList;
			if (part === '{{N}}') return <DaysInput
				key='days'
				type='number'
				min={0}
				step={1}
				value={Math.abs(stop.at)}
				onChange={e => {
					const days = parseInt(e.target.value, 10);
					if (!isNaN(days)) setDays(index, days);
				}}
			/>;
			const text = part.trim();
			return text ? <Unit key={`text-${p}`}>{text}</Unit> : null;
		});
	};

	return <FlexDiv flow='column' align='stretch' gap='12px'>
		<FlexDiv gap='8px' justify='space-between'>
			<Dropdown<ColorScaleKind>
				name='DD-color-scale-kind'
				datalist={kindOptions}
				selectedValue={kindOptions.find(option => option.value === scale.kind)}
				onChange={value => set({ kind: value.value })}
				readOnly
				dropdownStyle={{ width: '160px', optionWidth: '160px', containerHeight: '28px' }}
			/>
			<FlexDiv gap='6px'>
				<IconButton type='button' title={translateToString('color_scale.flip')} onClick={flip}><ArrowLeftRight size={14} /></IconButton>
				<IconButton type='button' title={translateToString('color_scale.reset')} onClick={() => { setSelected(0); setSides([]); props.onChange(undefined); }}><RotateCcw size={14} /></IconButton>
			</FlexDiv>
		</FlexDiv>
		<BarWrapper>
			<div ref={barRef}>
				<Bar
					style={{ background: cssGradient(scale) }}
					title={translateToString('color_scale.add_stop')}
					onClick={e => addStopAt(shareFromPointer(e.clientX))}
				/>
			</div>
			{stops.map((stop, i) => <Handle
				key={`stop-${i}`}
				color={stop.color}
				selected={i === selected}
				fixed={fixedOnBar(i)}
				style={{ left: `${shares[i].share * 100}%` }}
				onPointerDown={e => {
					e.stopPropagation();
					setSelected(i);
					if (!fixedOnBar(i)) e.currentTarget.setPointerCapture(e.pointerId);
				}}
				onPointerMove={e => {
					if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
					updateStop(i, { at: snapped(shareFromPointer(e.clientX)) });
				}}
				onPointerUp={e => { if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId); }}
			/>)}
		</BarWrapper>
		<FlexDiv flow='column' align='stretch' gap='4px'>
			<Label>{translateToString('color_scale.range')}</Label>
			<Dropdown<Placement>
				name='DD-color-scale-range'
				datalist={placementOptions}
				selectedValue={placementOptions.find(option => option.value === placementOf(scale))}
				onChange={value => place(value.value)}
				readOnly
				dropdownStyle={{ width: '100%', optionWidth: '100%', containerHeight: '28px' }}
			/>
		</FlexDiv>
		{scale.range && !atValues && <FlexDiv gap='8px'>
			<Label>{translateToString('color_scale.range_min')}</Label>
			<BoundInput
				type='number'
				step='any'
				value={scale.range.min}
				onChange={e => set({ range: { min: parseFloat(e.target.value) || 0, max: scale.range!.max } })}
			/>
			<Label>{translateToString('color_scale.range_max')}</Label>
			<BoundInput
				type='number'
				step='any'
				value={scale.range.max}
				onChange={e => set({ range: { min: scale.range!.min, max: parseFloat(e.target.value) || 0 } })}
			/>
		</FlexDiv>}
		<FlexDiv justify='space-between'>
			<Label>{translateToString('color_scale.stops')}</Label>
			<IconButton type='button' title={translateToString('color_scale.add_stop')} onClick={addStop}><Plus size={14} /></IconButton>
		</FlexDiv>
		<FlexDiv flow='column' align='stretch' gap='2px'>
			{stops.map((stop, i) => <StopRow key={`row-${i}`} selected={i === selected} style={{ order: shares[i].rank }} onClick={() => setSelected(i)}>
				<StopValueCell wide={dateRows}>
					{dateRows
						? dateStopCells(i, stop)
						: atValues
							? <StopValueInput
								type='number'
								step='any'
								value={stop.at}
								onChange={e => {
									const at = parseFloat(e.target.value);
									if (!isNaN(at)) updateStop(i, { at });
								}}
							/>
							: <>
								<StopValueInput
									type='number'
									min={0}
									max={100}
									value={Math.round(stop.at * 100)}
									onChange={e => {
										const at = parseInt(e.target.value, 10);
										if (!isNaN(at)) updateStop(i, { at: clamp01(at / 100) });
									}}
								/>
								<Unit>%</Unit>
							</>}
				</StopValueCell>
				<Swatch type='color' value={stop.color} onChange={e => updateStop(i, { color: e.target.value.toUpperCase() })} />
				<HexField color={stop.color} onChange={color => updateStop(i, { color })} />
				<div style={{ flexGrow: 1 }} />
				<IconButton type='button' disabled={stops.length <= 2} title={translateToString('color_scale.remove_stop')} onClick={() => removeStop(i)}><Minus size={14} /></IconButton>
			</StopRow>)}
		</FlexDiv>
		<Label>{translateToString('color_scale.presets')}</Label>
		<FlexDiv gap='8px' style={{ flexWrap: 'wrap' }}>
			{COLOR_SCALE_PRESETS.map(preset => <PresetChip
				key={preset.key}
				active={presetOf(scale) === preset.key}
				title={translateToString(`color_scale.preset_${preset.key}`)}
				style={{ background: cssGradient({ kind: scale.kind, range: null, stops: preset.stops }) }}
				onClick={() => applyPreset(preset.stops)}
			/>)}
		</FlexDiv>
	</FlexDiv>;
}
